import React, { useEffect, useState, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { ArrowLeft, ExternalLink, Tag, Pin, CalendarClock, FolderOpen, Link2, Type } from "lucide-react";
import DOMPurify from "dompurify";
import Header from "../../app/nav/Header";
import Login from "../../app/auth/Login";
import { getToken } from "../../app/auth/authConfig";
import axios from "axios";
const FileViewModal = lazy(() => import("../../components/modal/FileViewModal"));
const ProductDocumentActions = lazy(() => import("../../components/reuseable/ProductDocumentActions"));

const Label = ({ icon: Icon, text }) => (
  <span className="inline-flex items-center gap-1.5 text-[#7d1fa3]/85 text-xs uppercase tracking-[0.08em] font-semibold mb-2">
    <Icon className="w-3.5 h-3.5 text-[#E12AFB]" /> {text}
  </span>
);

const InfoValue = ({ children }) => <div className="text-[#2f1b39] text-[15px] font-medium break-words">{children || "-"}</div>;

const FieldSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-4 w-28 bg-[#7d1fa3]/15 rounded mb-2" />
    <div className="h-5 w-full max-w-xl bg-[#7d1fa3]/10 rounded" />
  </div>
);

const ToolDetailsSkeleton = () => (
  <div className="mt-2 grid grid-cols-1 xl:grid-cols-12 gap-5 animate-pulse">
    <aside className="xl:col-span-3 flex flex-col gap-5">
      <div className="rounded-2xl border border-[#7d1fa3]/10 bg-white/90 shadow-sm p-4">
        <div className="h-6 w-28 bg-[#7d1fa3]/15 rounded mb-4" />
        <div className="space-y-4">
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
        </div>
      </div>

      <div className="rounded-2xl border border-[#7d1fa3]/10 bg-white/90 shadow-sm p-4">
        <div className="h-6 w-28 bg-[#7d1fa3]/15 rounded mb-4" />
        <div className="space-y-3">
          <div className="h-4 w-36 bg-[#7d1fa3]/12 rounded" />
          <div className="h-8 w-full bg-[#7d1fa3]/10 rounded-xl" />
          <div className="h-8 w-11/12 bg-[#7d1fa3]/10 rounded-xl" />
          <div className="h-4 w-32 bg-[#7d1fa3]/12 rounded mt-2" />
          <div className="h-8 w-full bg-[#7d1fa3]/10 rounded-xl" />
        </div>
      </div>

      <div className="rounded-2xl border border-[#7d1fa3]/10 bg-white/85 shadow-sm p-4">
        <div className="h-4 w-24 bg-[#7d1fa3]/15 rounded mb-3" />
        <div className="space-y-3">
          <div className="h-10 w-full bg-[#7d1fa3]/10 rounded-xl" />
          <div className="h-10 w-full bg-[#7d1fa3]/10 rounded-xl" />
          <div className="h-10 w-full bg-[#7d1fa3]/10 rounded-xl" />
        </div>
      </div>

      <div className="rounded-2xl border border-[#7d1fa3]/10 bg-white/85 shadow-sm p-4">
        <div className="h-4 w-28 bg-[#7d1fa3]/15 rounded mb-3" />
        <ActionsFallback />
      </div>
    </aside>

    <div className="xl:col-span-9 flex flex-col gap-5">
      <div className="rounded-2xl border border-[#7d1fa3]/10 bg-white/85 shadow-sm p-5 sm:p-6">
        <div className="h-4 w-24 bg-[#7d1fa3]/15 rounded mb-3" />
        <div className="space-y-2 mb-6">
          <div className="h-4 w-full bg-[#7d1fa3]/10 rounded" />
          <div className="h-4 w-full bg-[#7d1fa3]/10 rounded" />
          <div className="h-4 w-11/12 bg-[#7d1fa3]/10 rounded" />
          <div className="h-4 w-10/12 bg-[#7d1fa3]/10 rounded" />
          <div className="h-4 w-9/12 bg-[#7d1fa3]/10 rounded" />
        </div>
        <div className="h-4 w-28 bg-[#7d1fa3]/15 rounded mb-3" />
        <div className="space-y-3 mb-6">
          <div className="h-6 w-1/3 bg-[#7d1fa3]/10 rounded" />
          <div className="h-56 w-full bg-[#7d1fa3]/10 rounded-xl" />
          <div className="h-4 w-40 bg-[#7d1fa3]/10 rounded" />
        </div>
        <div className="h-4 w-20 bg-[#7d1fa3]/15 rounded mb-3" />
        <div className="grid grid-cols-2 gap-3">
          <div className="h-10 w-full bg-[#7d1fa3]/10 rounded-xl" />
          <div className="h-10 w-full bg-[#7d1fa3]/10 rounded-xl" />
          <div className="h-10 w-full bg-[#7d1fa3]/10 rounded-xl" />
          <div className="h-10 w-full bg-[#7d1fa3]/10 rounded-xl" />
        </div>
      </div>
    </div>
  </div>
);

const DescriptionFallback = () => (
  <div className="space-y-2 animate-pulse">
    <div className="h-4 w-full bg-[#7d1fa3]/10 rounded" />
    <div className="h-4 w-11/12 bg-[#7d1fa3]/10 rounded" />
    <div className="h-4 w-4/5 bg-[#7d1fa3]/10 rounded" />
  </div>
);

const ActionsFallback = () => (
  <div className="flex gap-2 mt-2 sm:mt-0 animate-pulse">
    <div className="h-10 w-28 rounded-xl bg-[#7d1fa3]/10" />
    <div className="h-10 w-48 rounded-xl bg-[#7d1fa3]/10" />
    <div className="h-10 w-44 rounded-xl bg-[#7d1fa3]/10" />
  </div>
);

const parseFileList = (value) => {
  if (!value) return [];
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "-";
  return parsedDate.toLocaleString();
};

const ToolDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFileViewModal, setShowFileViewModal] = useState(false);
  const [showUrlPopup, setShowUrlPopup] = useState(false);
  const [activePreviewFile, setActivePreviewFile] = useState("");
  const [selectedProductFile, setSelectedProductFile] = useState("");
  const [selectedOnboardingFile, setSelectedOnboardingFile] = useState("");

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        setError("");
        const token = await getToken();
        const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/${id}`;
        const res = await axios.get(baseURL, {
          headers: { "oidc-token": token },
        });
        setDetails(res.data);
      } catch (err) {
        console.error("Tool details fetch error", err);
        setError("Failed to load tool details");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchDetails();
    }
  }, [id]);

  const productFiles = parseFileList(details?.filePath);
  const onboardingFiles = parseFileList(details?.onboardingFilePath);
  const safeDescriptionHtml = DOMPurify.sanitize(details?.description || "", {
    USE_PROFILES: { html: true },
  });

  const primaryProductFile = productFiles[0] || null;
  const primaryOnboardingFile = onboardingFiles[0] || null;
  useEffect(() => {
    if (productFiles.length > 0) {
      setSelectedProductFile((prev) => (prev && productFiles.includes(prev) ? prev : productFiles[0]));
    } else {
      setSelectedProductFile("");
    }
  }, [productFiles]);

  useEffect(() => {
    if (onboardingFiles.length > 0) {
      setSelectedOnboardingFile((prev) => (prev && onboardingFiles.includes(prev) ? prev : onboardingFiles[0]));
    } else {
      setSelectedOnboardingFile("");
    }
  }, [onboardingFiles]);

  const buildDownloadUrl = (filename) =>
    `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/download/${encodeURIComponent(filename)}`;

  const productDownloadUrl = selectedProductFile ? buildDownloadUrl(selectedProductFile) : null;
  const onboardingDownloadUrl = selectedOnboardingFile ? buildDownloadUrl(selectedOnboardingFile) : null;

  const handleViewProductFile = () => {
    if (!selectedProductFile) return;
    setActivePreviewFile(selectedProductFile);
    setShowFileViewModal(true);
  };

  const handleViewOnboardingFile = () => {
    if (!selectedOnboardingFile) return;
    setActivePreviewFile(selectedOnboardingFile);
    setShowFileViewModal(true);
  };

  return (
    <>
      <AuthenticatedTemplate>
        <div className="w-full min-h-screen flex flex-col bg-[radial-gradient(circle_at_15%_10%,_#f7ddff_0,_#f4f7ff_35%,_#e9fbff_70%,_#f8feff_100%)]">
          <Header />
          <div className="w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
            <div className="rounded-3xl bg-white/80 backdrop-blur-xl border border-white/70 shadow-[0_20px_60px_-30px_rgba(67,24,103,0.55)] relative overflow-hidden">
              <div className="absolute -top-20 -left-20 w-56 h-56 bg-[#E12AFB]/10 rounded-full blur-3xl z-0" />
              <div className="absolute -bottom-24 -right-16 w-72 h-72 bg-[#22c1c3]/10 rounded-full blur-3xl z-0" />

              <div className="relative z-10 px-5 sm:px-8 pt-6 sm:pt-8 pb-4 border-b border-[#7d1fa3]/10">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="space-y-2">
                    <button
                      onClick={() => navigate("/application")}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/85 border border-[#7d1fa3]/15 hover:bg-white transition-colors text-[#7d1fa3] text-sm font-medium"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back to Applications
                    </button>
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#5e1783] tracking-tight">Tool Intelligence Card</h1>
                    <p className="text-[#6f4a86] text-sm sm:text-base max-w-2xl">
                      Review tool profile, rich description, web preview, and all downloadable resources from a single workspace.
                    </p>
                  </div>
                </div>
              </div>

              <div className="relative z-10 p-5 sm:p-8">

                {isLoading && <ToolDetailsSkeleton />}

                {!isLoading && error && (
                  <div className="py-8 text-red-500 z-10 relative">
                    <p>{error}</p>
                  </div>
                )}

                {!isLoading && !error && details && (
                  <div className="mt-2 grid grid-cols-1 xl:grid-cols-12 gap-5">
                    <aside className="xl:col-span-3 flex flex-col gap-5">

                       <section className="rounded-2xl border border-[#7d1fa3]/10 bg-white/90 shadow-sm p-4 sm:p-4">
                        <h2 className="text-[#5e1783] text-lg font-semibold mb-4">Overview</h2>

                        <div className="space-y-4">
                          <div>
                            <Label icon={Type} text="Name" />
                            <InfoValue>{details.toolName}</InfoValue>
                          </div>
                          <div>
                            <Label icon={Tag} text="Type" />
                            <InfoValue>{details.tooltype}</InfoValue>
                          </div>
                          <div>
                            <Label icon={Pin} text="Tags" />
                            <InfoValue>{details.tags}</InfoValue>
                          </div>
                          <div>
                            <Label icon={CalendarClock} text="Created At" />
                            <InfoValue>{formatDate(details.createdAt)}</InfoValue>
                          </div>
                        </div>
                      </section>

                      <section className="rounded-2xl border border-[#7d1fa3]/10 bg-white/90 shadow-sm p-4 sm:p-4">
                        <h2 className="text-[#5e1783] text-lg font-semibold mb-4">Resources</h2>

                        <div className="space-y-4">
                          <div>
                            <Label icon={FolderOpen} text="Product Documents" />
                            {productFiles.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {productFiles.map((file, index) => (
                                  <a
                                    key={`${file}-${index}`}
                                    href={buildDownloadUrl(file)}
                                    download
                                    className="px-3 py-1.5 rounded-full bg-[#E12AFB]/10 text-[#7d1fa3] text-xs font-semibold hover:bg-[#E12AFB]/20 transition-colors"
                                  >
                                    Product File {index + 1}
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <InfoValue>-</InfoValue>
                            )}
                          </div>

                          <div>
                            <Label icon={FolderOpen} text="Onboarding Files" />
                            {onboardingFiles.length > 0 ? (
                              <div className="flex flex-wrap gap-2">
                                {onboardingFiles.map((file, index) => (
                                  <a
                                    key={`${file}-${index}`}
                                    href={buildDownloadUrl(file)}
                                    download
                                    className="px-3 py-1.5 rounded-full bg-[#34d399]/15 text-[#065f46] text-xs font-semibold hover:bg-[#34d399]/25 transition-colors"
                                  >
                                    Onboarding File {index + 1}
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <InfoValue>-</InfoValue>
                            )}
                          </div>
                        </div>
                      </section>

                      <section className="rounded-2xl border border-[#7d1fa3]/10 bg-white/85 shadow-sm p-4 sm:p-4">
                        <Label icon={Link2} text="Live URL" />
                        {details.url ? (
                          <div className="flex flex-col gap-3">
                            <button
                              onClick={() => setShowUrlPopup(true)}
                              className="w-fit px-4 py-2 rounded-xl bg-gradient-to-r from-[#7d1fa3] to-[#E12AFB] hover:from-[#6a178f] hover:to-[#cb24e5] text-white text-sm font-semibold transition-colors"
                            >
                              Open Live URL in Popup
                            </button>
                            <a
                              href={details.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-500 inline-flex items-center gap-1 font-medium w-fit"
                            >
                              Open Link in New Tab
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          </div>
                        ) : (
                          <InfoValue>-</InfoValue>
                        )}
                      </section>

                        <section className="rounded-2xl border border-[#7d1fa3]/10 bg-white/85 shadow-sm p-4 sm:p-4">
                          <Label icon={FolderOpen} text="Quick Actions" />
                          <Suspense fallback={<ActionsFallback />}>
                            <ProductDocumentActions
                              productFiles={productFiles}
                              onboardingFiles={onboardingFiles}
                              selectedProductFile={selectedProductFile}
                              setSelectedProductFile={setSelectedProductFile}
                              selectedOnboardingFile={selectedOnboardingFile}
                              setSelectedOnboardingFile={setSelectedOnboardingFile}
                              productDownloadUrl={productDownloadUrl}
                              onboardingDownloadUrl={onboardingDownloadUrl}
                              onViewProductFile={handleViewProductFile}
                              onViewOnboardingFile={handleViewOnboardingFile}
                              canViewProductFile={Boolean(primaryProductFile)}
                              canViewOnboardingFile={Boolean(primaryOnboardingFile)}
                            />
                          </Suspense>
                      </section>
                    </aside>

                    <div className="xl:col-span-9 flex flex-col gap-5">
                      <section className="rounded-2xl border border-[#7d1fa3]/10 bg-white/85 shadow-sm p-5 sm:p-6">
                        <Label icon={Pin} text="Description" />
                        <div
                          className="text-[#3a3a3a] text-base break-words leading-relaxed min-h-[120px] [&_p]:mb-2 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_strong]:text-[#7d1fa3] [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded-lg"
                          dangerouslySetInnerHTML={{ __html: safeDescriptionHtml }}
                        />
                        {!safeDescriptionHtml && (
                          <Suspense fallback={<DescriptionFallback />}>
                            <DescriptionFallback />
                          </Suspense>
                        )}
                      </section>

                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {showFileViewModal && activePreviewFile && (
            <Suspense
              fallback={
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-6 shadow-xl text-[#7d1fa3] font-medium">Loading file preview...</div>
                </div>
              }
            >
              <FileViewModal
                filePath={activePreviewFile}
                fileName={details.toolName}
                onClose={() => {
                  setShowFileViewModal(false);
                  setActivePreviewFile("");
                }}
              />
            </Suspense>
          )}

          {showUrlPopup && details?.url && (
            <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 px-4">
              <div className="w-full max-w-[80%] h-[85vh] bg-white rounded-2xl shadow-2xl border border-[#7d1fa3]/15 overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#7d1fa3]/10 bg-[#f9f4ff]">
                  <h3 className="text-[#5e1783] font-semibold text-sm sm:text-base">Live URL Preview</h3>
                  <div className="flex items-center gap-2">
                    <a
                      href={details.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 text-xs sm:text-sm font-semibold hover:bg-blue-200 transition-colors"
                    >
                      Open in New Tab
                    </a>
                    <button
                      onClick={() => setShowUrlPopup(false)}
                      className="px-3 py-1.5 rounded-lg bg-[#7d1fa3]/10 text-[#7d1fa3] text-xs sm:text-sm font-semibold hover:bg-[#7d1fa3]/20 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>

                <iframe
                  src={details.url}
                  title="Tool Live URL Popup"
                  className="w-full flex-1"
                  style={{ background: "#fff" }}
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                />
              </div>
            </div>
          )}
        </div>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Login />
      </UnauthenticatedTemplate>
    </>
  );
};
export default ToolDetails;
